/**
 * Apps Grid Page
 *
 * The "Toolbox" interface showing all ContentSage AI Suite apps.
 * This is the new home base for mobile/web users (not Wix).
 *
 * Apps:
 * - BlogCraft (Blog Generation)
 * - PlanCraft (Strategy/BMC Engine)
 * - Planner (Editorial Calendar)
 * - SEO Booster (Coming Soon)
 */

import React from 'react';
import { motion } from 'framer-motion';
import {
  FileText,
  Brain,
  Calendar,
  Search,
  Sparkles,
  ArrowRight,
  Target,
  Building2,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { ContentSageIcon } from '../ContentSageLogo';
import { CreditsBadge } from './CreditsBadge';

export type AppId = 'blogcraft' | 'plancraft' | 'calendar' | 'seobooster' | 'brand-profiles';

interface AppConfig {
  id: AppId;
  name: string;
  tagline: string;
  description: string;
  icon: React.ElementType;
  gradient: string;
  iconBg: string;
  available: boolean;
  badge?: string;
  agencyOnly?: boolean;
}

const SUITE_APPS: AppConfig[] = [
  {
    id: 'blogcraft',
    name: 'BlogCraft',
    tagline: 'AI Blog Writer',
    description: 'Generate SEO-optimized blog posts with research-backed content',
    icon: FileText,
    gradient: 'from-indigo-600 to-purple-600',
    iconBg: 'bg-indigo-100 text-indigo-600',
    available: true,
  },
  {
    id: 'plancraft',
    name: 'PlanCraft',
    tagline: 'Strategy Engine',
    description: 'Build your Business Model Canvas with AI-guided interviews',
    icon: Brain,
    gradient: 'from-emerald-500 to-teal-600',
    iconBg: 'bg-emerald-100 text-emerald-600',
    available: true,
    badge: 'NEW',
  },
  {
    id: 'calendar',
    name: 'Planner',
    tagline: 'Editorial Calendar',
    description: 'Plan and schedule your content strategy for maximum impact',
    icon: Calendar,
    gradient: 'from-amber-500 to-orange-600',
    iconBg: 'bg-amber-100 text-amber-600',
    available: true,
    badge: 'NEW',
  },
  {
    id: 'seobooster',
    name: 'SEO Booster',
    tagline: 'Keyword Research',
    description: 'Discover high-impact keywords and optimize your content',
    icon: Search,
    gradient: 'from-pink-500 to-rose-600',
    iconBg: 'bg-pink-100 text-pink-600',
    available: false,
    badge: 'SOON',
  },
  {
    id: 'brand-profiles',
    name: 'Brand Profiles',
    tagline: 'Agency Feature',
    description: 'Manage multiple brand voices and styles for your clients',
    icon: Building2,
    gradient: 'from-violet-500 to-purple-600',
    iconBg: 'bg-violet-100 text-violet-600',
    available: true,
    badge: 'AGENCY',
    agencyOnly: true,
  },
];

interface AppsGridPageProps {
  onAppSelect: (appId: AppId) => void;
  userPlan?: 'free' | 'starter' | 'professional' | 'agency';
  credits?: number;
  onNavigateToPricing?: () => void;
}

export const AppsGridPage: React.FC<AppsGridPageProps> = ({
  onAppSelect,
  userPlan = 'free',
  credits = 0,
  onNavigateToPricing,
}) => {
  const { user } = useAuth();
  const firstName = user?.displayName?.split(' ')[0] || 'there';

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <div className="px-4 py-6 space-y-6 pb-24">
      {/* Header */}
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <ContentSageIcon size={28} className="rounded-lg" />
          <span className="text-xs font-semibold text-indigo-600 uppercase tracking-wider">
            AI Suite
          </span>
        </div>
        <h1 className="text-2xl font-bold text-gray-900">
          Welcome, {firstName}!
        </h1>
        <p className="text-gray-500">Choose your AI-powered tool</p>
      </div>

      {/* Credits/Plan Banner */}
      <CreditsBadge
        credits={credits}
        plan={userPlan}
        compact={false}
        onUpgrade={onNavigateToPricing}
      />

      {/* Apps Grid */}
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="grid grid-cols-2 gap-4"
      >
        {SUITE_APPS
          .filter((app) => !app.agencyOnly || userPlan === 'agency')
          .map((app) => (
          <motion.button
            key={app.id}
            variants={cardVariants}
            whileTap={{ scale: app.available ? 0.98 : 1 }}
            onClick={() => app.available && onAppSelect(app.id)}
            disabled={!app.available}
            className={`relative bg-white rounded-2xl p-4 shadow-sm border border-gray-100 text-left transition-all ${
              app.available
                ? 'hover:shadow-md hover:border-gray-200 active:bg-gray-50'
                : 'opacity-60 cursor-not-allowed'
            }`}
          >
            {/* Badge */}
            {app.badge && (
              <span className={`absolute -top-2 -right-2 text-[10px] font-bold px-2 py-0.5 rounded-full ${
                app.badge === 'NEW'
                  ? 'bg-emerald-500 text-white'
                  : 'bg-gray-400 text-white'
              }`}>
                {app.badge}
              </span>
            )}

            {/* Icon */}
            <div className={`w-12 h-12 ${app.iconBg} rounded-xl flex items-center justify-center mb-3`}>
              <app.icon className="w-6 h-6" />
            </div>

            {/* Content */}
            <h3 className="font-bold text-gray-900">{app.name}</h3>
            <p className="text-xs text-gray-500 mt-0.5">{app.tagline}</p>
            <p className="text-xs text-gray-400 mt-2 line-clamp-2">{app.description}</p>

            {/* Arrow indicator for available apps */}
            {app.available && (
              <div className="absolute bottom-4 right-4">
                <ArrowRight className="w-4 h-4 text-gray-300" />
              </div>
            )}
          </motion.button>
        ))}
      </motion.div>

      {/* Quick Tips Section */}
      <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-100">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-indigo-100 rounded-lg">
            <Sparkles className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <h4 className="font-semibold text-indigo-900">Pro Tip</h4>
            <p className="text-sm text-indigo-700 mt-1">
              Start with <strong>PlanCraft</strong> to define your business strategy,
              then use <strong>Planner</strong> to schedule content, and finally
              generate articles with <strong>BlogCraft</strong>.
            </p>
          </div>
        </div>
      </div>

      {/* Workflow Banner */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Target className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900">Smart Workflow</h4>
              <p className="text-xs text-gray-500">Strategy → Plan → Create → Publish</p>
            </div>
          </div>
          <div className="flex -space-x-2">
            <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center border-2 border-white">
              <Brain className="w-4 h-4 text-emerald-600" />
            </div>
            <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center border-2 border-white">
              <Calendar className="w-4 h-4 text-amber-600" />
            </div>
            <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center border-2 border-white">
              <FileText className="w-4 h-4 text-indigo-600" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppsGridPage;
